import type {
  QueryReference,
  QueryReferenceType,
} from '@agentselly/immodossier-shared';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import {
  shouldAutoRetryAuth,
  clearAuthAutoRetryFlagIfEpisodeOver,
} from '~/helper/authAutoRetry.ts';

export function useBackendQuery<T extends QueryReference>(
  key: T,
  options?: Options<QueryReferenceType<T>>,
) {
  const router = useRouter();

  return useQuery({
    queryKey: [key],
    queryFn: async function () {
      // Pass query parameters to the backend
      const url = new URL(location.href);

      url.pathname = `/query/${key}`;

      const response = await fetchBackend(url, {
        method: options?.body ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
      });

      if (!response.ok) {
        console.error(
          'useBackendQuery Error',
          response.status,
          response.statusText,
          await response.text(),
        );

        if (response.status === 401) {
          // Auto-retry: if the session token wasn't propagated after Clerk OAuth,
          // a single page reload resolves it. Guard ensures at most ONE reload.
          if (shouldAutoRetryAuth(Date.now(), sessionStorage)) {
            window.location.reload();
            return options?.defaultValue || undefined;
          }

          // Redirect to login page via router so the history stack is preserved
          void router.navigate({ to: '/auth/login', replace: true });
          return options?.defaultValue || undefined;
        }

        // 403 is intentionally NOT handled here: useBackendQuery is used for
        // non-critical data queries where a 403 falls into the generic error
        // path (returning defaultValue). Auth auto-retry for 403 is only
        // needed in useLoaderQuery which drives page-level loaders.

        return options?.defaultValue || undefined;
      }

      // Happy path: successful response — clear auto-retry flag only if the
      // current episode has expired. This prevents a parallel 401 loader from
      // having its flag cleared mid-episode, which was the root cause of the
      // infinite reload loop (max 1 reload per 10-minute episode).
      clearAuthAutoRetryFlagIfEpisodeOver(Date.now(), sessionStorage);

      return (await response.json()) as QueryReferenceType<T>;
    },
    refetchInterval: options?.refetchInterval,
  });
}

interface Options<T> {
  readonly body?: any;
  readonly defaultValue?: T;
  readonly refetchInterval?: number;
}
